import type { ResumeData, ResumeSettings } from '../types/resume';
import { mergeSettings, formatDate, pagePadding } from '../lib/resume-settings';

interface StandardTemplateProps {
  data: ResumeData;
  settings?: ResumeSettings;
}

const sectionTitles: Record<string, string> = {
  summary: 'Professional Summary',
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
};

function Description({ text, settings }: { text: string; settings: ResumeSettings }) {
  if (!text) return null;
  const bScale = settings.sizes.body / 100;
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length <= 1) {
    return <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: settings.spacing.lineSpacing }}>{text}</p>;
  }
  return (
    <ul className="list-disc pl-5 space-y-1 text-slate-700" style={{ fontSize: `${0.95 * bScale}rem` }}>
      {lines.map((line, i) => <li key={i} style={{ lineHeight: settings.spacing.lineSpacing }}>{line.replace(/^[-•\s]+/, '')}</li>)}
    </ul>
  );
}

export function StandardTemplate({ data, settings }: StandardTemplateProps) {
  const { personal, experience, education, skills, projects, certifications } = data;
  const s = mergeSettings(settings);
  const hScale = s.sizes.heading / 100;
  const bScale = s.sizes.body / 100;

  const order = s.sectionOrder && s.sectionOrder.length > 0 ? s.sectionOrder : ['summary', 'experience', 'education', 'skills', 'projects', 'certifications'];
  const contact = [personal.email, personal.phone, personal.location, personal.website, personal.linkedin].filter(Boolean);

  const renderSection = (section: string) => {
    switch (section) {
      case 'summary':
        return personal.summary ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <p className="text-slate-700 whitespace-pre-line" style={{ fontSize: `${0.95 * bScale}rem`, lineHeight: s.spacing.lineSpacing }}>{personal.summary}</p>
          </section>
        ) : null;
      case 'experience':
        return experience.length > 0 ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <SectionTitle title={sectionTitles.experience} s={s} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {experience.map((exp) => (
                <article key={exp.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{exp.title}</h3>
                    <span className="text-slate-500 shrink-0" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(exp.startDate, s.dateFormat)} — {exp.current ? 'Present' : formatDate(exp.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="font-medium text-slate-700 mb-1" style={{ fontSize: `${0.875 * bScale}rem` }}>{exp.company}{exp.location && `, ${exp.location}`}</p>
                  <Description text={exp.description} settings={s} />
                </article>
              ))}
            </div>
          </section>
        ) : null;
      case 'education':
        return education.length > 0 ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <SectionTitle title={sectionTitles.education} s={s} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {education.map((edu) => (
                <article key={edu.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{edu.school}</h3>
                    <span className="text-slate-500 shrink-0" style={{ fontSize: `${0.875 * bScale}rem` }}>
                      {formatDate(edu.startDate, s.dateFormat)} — {formatDate(edu.endDate, s.dateFormat)}
                    </span>
                  </div>
                  <p className="text-slate-700" style={{ fontSize: `${0.875 * bScale}rem` }}>{edu.degree}{edu.field && `, ${edu.field}`}</p>
                  {edu.description && <Description text={edu.description} settings={s} />}
                </article>
              ))}
            </div>
          </section>
        ) : null;
      case 'skills':
        return skills.length > 0 ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <SectionTitle title={sectionTitles.skills} s={s} />
            <p className="text-slate-700" style={{ fontSize: `${0.95 * bScale}rem` }}>{skills.map((skill) => skill.name).join(' • ')}</p>
          </section>
        ) : null;
      case 'projects':
        return projects.length > 0 ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <SectionTitle title={sectionTitles.projects} s={s} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {projects.map((proj) => (
                <article key={proj.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="font-semibold text-slate-900" style={{ fontSize: `${1.05 * hScale}rem` }}>{proj.name}</h3>
                    {proj.url && <a href={proj.url} className="text-slate-500 hover:underline truncate max-w-[40%]" style={{ fontSize: `${0.75 * bScale}rem` }}>{proj.url}</a>}
                  </div>
                  <Description text={proj.description} settings={s} />
                  {proj.technologies.length > 0 && (
                    <p className="text-slate-600 mt-1" style={{ fontSize: `${0.75 * bScale}rem` }}><span className="font-semibold">Technologies:</span> {proj.technologies.join(', ')}</p>
                  )}
                </article>
              ))}
            </div>
          </section>
        ) : null;
      case 'certifications':
        return certifications.length > 0 ? (
          <section key={section} style={{ marginBottom: s.spacing.sectionGap }}>
            <SectionTitle title={sectionTitles.certifications} s={s} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: s.spacing.contentGap }}>
              {certifications.map((cert) => (
                <div key={cert.id} className="flex items-baseline justify-between gap-3">
                  <span className="font-medium text-slate-800" style={{ fontSize: `${0.95 * bScale}rem` }}>{cert.name}</span>
                  <span className="text-slate-500 shrink-0" style={{ fontSize: `${0.875 * bScale}rem` }}>{cert.issuer}{cert.date && ` • ${cert.date}`}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div
      className="bg-white text-slate-900 h-full"
      style={{
        fontFamily: `${s.fonts.primary || 'Inter'}, system-ui, sans-serif`,
        padding: pagePadding(s.margins),
        lineHeight: s.spacing.lineSpacing,
        fontSize: `${bScale}rem`,
      }}
    >
      <header className="border-b-2 border-slate-900 pb-4 mb-6" style={{ textAlign: s.headerAlignment }}>
        {s.photo && <img src={s.photo} alt="" className="w-20 h-20 rounded-full object-cover mx-auto mb-3 border-2 border-slate-900" />}
        <h1
          className="font-bold text-slate-900 tracking-tight"
          style={{ fontFamily: `${s.fonts.secondary || s.fonts.primary || 'Inter'}, system-ui, sans-serif`, fontSize: `${2.25 * hScale}rem` }}
        >
          {personal.fullName || 'Your Name'}
        </h1>
        {contact.length > 0 && <p className="text-slate-600 mt-2" style={{ fontSize: `${0.875 * bScale}rem` }}>{contact.join(' • ')}</p>}
      </header>

      <main>
        {order.map((section) => renderSection(section))}
      </main>
    </div>
  );
}

function SectionTitle({ title, s }: { title: string; s: ResumeSettings }) {
  const hScale = s.sizes.heading / 100;
  return (
    <h2
      className="font-bold text-slate-900 uppercase tracking-wide border-b-2 border-slate-900 pb-1 mb-3"
      style={{ fontSize: `${1.15 * hScale}rem` }}
    >
      {title}
    </h2>
  );
}
